/**
 * Функции для страницы Explore (лента профилей, лайки, матчи)
 */

import { supabase } from './supabase'
import { getFinalScore, getCompatibilityLabel, getCompatibilityColor, getCompatibilityBgColor } from './compatibility'
import { getZodiacSign } from '../utils/zodiac'

function calculateAge(dateOfBirth) {
  if (!dateOfBirth) return null
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

function getZodiacFromDate(dateOfBirth) {
  if (!dateOfBirth) return { name: 'Unknown', symbol: '?' }
  const date = new Date(dateOfBirth)
  return getZodiacSign(date.getDate(), date.getMonth() + 1)
}

/** Сколько карточек стараемся отдать после исключения лайков/дизлайков/матчей */
const EXPLORE_TARGET_COUNT = 50
/** Размер одной пачки из БД (PostgREST range inclusive) */
const EXPLORE_BATCH_SIZE = 100
/** Максимум строк профилей просмотреть за один заход (не грузить всю таблицу) */
const EXPLORE_MAX_SCAN = 500

/**
 * Загрузить профили для Explore
 * @param {string} currentUserId - ID текущего пользователя
 * @param {string} lookingFor - 'male', 'female', 'both'
 * @returns {Promise<Array>} Массив профилей с совместимостью
 */
export async function loadExploreProfiles(currentUserId, lookingFor) {
  try {
    const { data: currentProfile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', currentUserId)
      .single()

    if (profileError || !currentProfile) {
      console.error('[loadExploreProfiles] Error loading current profile:', profileError)
      return []
    }

    const actualLookingFor = lookingFor || currentProfile.looking_for || 'both'
    let genderFilter = null
    if (actualLookingFor === 'male') genderFilter = 'male'
    else if (actualLookingFor === 'female') genderFilter = 'female'

    const { data: likes } = await supabase
      .from('likes')
      .select('to_user_id')
      .eq('from_user_id', currentUserId)
    const likedIds = new Set((likes || []).map((l) => l.to_user_id))

    const { data: dislikes } = await supabase
      .from('dislikes')
      .select('to_user_id')
      .eq('from_user_id', currentUserId)
    const dislikedIds = new Set((dislikes || []).map((d) => d.to_user_id))

    const { data: matches } = await supabase
      .from('matches')
      .select('user1_id, user2_id')
      .or(`user1_id.eq.${currentUserId},user2_id.eq.${currentUserId}`)
    const matchedIds = new Set()
    if (matches) {
      matches.forEach((m) => {
        if (m.user1_id === currentUserId) matchedIds.add(m.user2_id)
        else matchedIds.add(m.user1_id)
      })
    }

    const isProfileEligible = (p) =>
      !likedIds.has(p.id) && !dislikedIds.has(p.id) && !matchedIds.has(p.id)

    const collected = []
    let offset = 0

    while (collected.length < EXPLORE_TARGET_COUNT && offset < EXPLORE_MAX_SCAN) {
      const rangeEnd = offset + EXPLORE_BATCH_SIZE - 1
      let q = supabase
        .from('profiles')
        .select('*')
        .neq('id', currentUserId)
        .order('created_at', { ascending: false })
        .range(offset, rangeEnd)

      if (genderFilter) {
        q = q.eq('gender', genderFilter)
      }

      const { data: batch, error: batchError } = await q
      if (batchError) {
        console.error('[loadExploreProfiles] Batch error:', batchError)
        break
      }
      if (!batch?.length) break

      for (const p of batch) {
        if (!isProfileEligible(p)) continue
        collected.push(p)
        if (collected.length >= EXPLORE_TARGET_COUNT) break
      }

      if (batch.length < EXPLORE_BATCH_SIZE) break
      offset += EXPLORE_BATCH_SIZE
    }

    const filtered = collected

    const currentZodiac = getZodiacFromDate(currentProfile.date_of_birth)

    const processedPhotos = (photos) => {
      if (!Array.isArray(photos) || photos.length === 0) return []
      return photos.map((photo) => {
        if (!photo) return null
        if (typeof photo === 'string' && (photo.startsWith('http://') || photo.startsWith('https://'))) {
          return photo
        }
        const { data } = supabase.storage.from('photos').getPublicUrl(photo)
        return data?.publicUrl || null
      }).filter(Boolean)
    }

    return filtered.map((p) => {
      const zodiac = getZodiacFromDate(p.date_of_birth)
      const score = getFinalScore(currentZodiac.name, zodiac.name, currentUserId, p.id)
      return {
        ...p,
        photos: processedPhotos(p.photos),
        age: calculateAge(p.date_of_birth),
        zodiac: zodiac.name,
        zodiacSymbol: zodiac.symbol,
        compatibilityScore: score,
        compatibilityLabel: getCompatibilityLabel(score),
        compatibilityColor: getCompatibilityColor(score),
        compatibilityBgColor: getCompatibilityBgColor(score)
      }
    })
  } catch (error) {
    console.error('[loadExploreProfiles]', error)
    return []
  }
}

/**
 * Сохранить лайк и при взаимности создать матч и чат
 */
export async function saveLike(fromUserId, toUserId, isSuperLike = false) {
  try {
    const { error: likeError } = await supabase
      .from('likes')
      .insert({
        from_user_id: fromUserId,
        to_user_id: toUserId,
        is_super_like: isSuperLike
      })

    if (likeError) {
      return { match: false, error: likeError.message }
    }

    // Отправляем email уведомление о лайке/суперлайке
    const { sendEmailNotification } = await import('./sendEmailNotification')
    if (isSuperLike) {
      sendEmailNotification('new_super_like', toUserId, { fromUserId }).catch(err => 
        console.error('[saveLike] Email notification error:', err)
      )
    } else {
      sendEmailNotification('new_like', toUserId, { fromUserId }).catch(err => 
        console.error('[saveLike] Email notification error:', err)
      )
    }

    const { data: mutualLikes } = await supabase
      .from('likes')
      .select('id')
      .eq('from_user_id', toUserId)
      .eq('to_user_id', fromUserId)
      .limit(1)

    const hasMutual = mutualLikes && mutualLikes.length > 0
    if (!hasMutual) {
      return { match: false, error: null }
    }

    const user1Id = fromUserId < toUserId ? fromUserId : toUserId
    const user2Id = fromUserId < toUserId ? toUserId : fromUserId

    const { data: existingMatch } = await supabase
      .from('matches')
      .select('id')
      .eq('user1_id', user1Id)
      .eq('user2_id', user2Id)
      .maybeSingle()

    if (existingMatch) {
      return { match: true, error: null }
    }

    const { data: matchData, error: matchError } = await supabase
      .from('matches')
      .insert({ user1_id: user1Id, user2_id: user2Id })
      .select()
      .single()

    if (matchError || !matchData) {
      return { match: false, error: matchError?.message || 'Failed to create match' }
    }

    const { data: existingChat } = await supabase
      .from('chats')
      .select('id')
      .eq('match_id', matchData.id)
      .maybeSingle()

    if (!existingChat) {
      await supabase.from('chats').insert({
        match_id: matchData.id,
        user1_id: user1Id,
        user2_id: user2Id
      })
    }

    // Отправляем email уведомления о матче обоим пользователям
    sendEmailNotification('new_match', user1Id, { otherUserId: user2Id }).catch(err => 
      console.error('[saveLike] Match email notification error:', err)
    )
    sendEmailNotification('new_match', user2Id, { otherUserId: user1Id }).catch(err => 
      console.error('[saveLike] Match email notification error:', err)
    )

    return { match: true, error: null }
  } catch (error) {
    console.error('[saveLike]', error)
    return { match: false, error: error?.message }
  }
}

/**
 * Сохранить дизлайк
 */
export async function saveDislike(fromUserId, toUserId) {
  try {
    await supabase.from('dislikes').insert({
      from_user_id: fromUserId,
      to_user_id: toUserId
    })
  } catch (error) {
    console.error('[saveDislike]', error)
    throw error
  }
}

function getTodayDateString(timezone = 'UTC') {
  try {
    return new Date().toLocaleDateString('en-CA', { timeZone: timezone })
  } catch (_) {
    return new Date().toISOString().slice(0, 10)
  }
}

/**
 * Время до сброса дневного лимита (следующая полночь в таймзоне пользователя).
 * @param {string} timezone - IANA timezone (например 'Europe/Moscow')
 * @returns {string} Текст вида "in 5 hours 30 minutes" или "in 45 minutes"
 */
export function getSuperLikeResetTimeString(timezone = 'UTC') {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en', { timeZone: timezone, hour: 'numeric', minute: 'numeric', hour12: false })
    const parts = formatter.formatToParts(now)
    const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0)
    const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0)
    const totalMinutes = hour * 60 + minute
    const remainingMinutes = 24 * 60 - totalMinutes
    const h = Math.floor(remainingMinutes / 60)
    const m = remainingMinutes % 60
    if (h > 0 && m > 0) return `in ${h} hour${h !== 1 ? 's' : ''} and ${m} minute${m !== 1 ? 's' : ''}`
    if (h > 0) return `in ${h} hour${h !== 1 ? 's' : ''}`
    if (m > 0) return `in ${m} minute${m !== 1 ? 's' : ''}`
    return 'soon'
  } catch (_) {
    return 'in 24 hours'
  }
}

/**
 * Лимит лайков: бесплатный муж. 5/день, платный и женский — анлим
 */
export async function checkDailyLikeLimit(userId, timezone = 'UTC') {
  const dateStr = getTodayDateString(timezone)
  const { data: profile } = await supabase
    .from('profiles')
    .select('gender, subscription_status')
    .eq('id', userId)
    .single()

  const isFemale = profile?.gender === 'female'
  const hasSubscription = profile?.subscription_status === 'active' || profile?.subscription_status === 'trial'
  const limit = isFemale || hasSubscription ? 999 : 5

  const { data: row } = await supabase
    .from('daily_limits')
    .select('likes_count')
    .eq('user_id', userId)
    .eq('date', dateStr)
    .maybeSingle()

  const used = row?.likes_count ?? 0
  const remaining = Math.max(0, limit - used)
  return { limit, used, remaining, canLike: remaining > 0 }
}

/**
 * Суперлайки по ТЗ §26: бесплатный муж. 0, платный и женский — 3/день
 */
export async function checkDailySuperLikeLimit(userId, timezone = 'UTC') {
  const dateStr = getTodayDateString(timezone)
  const { data: profile } = await supabase
    .from('profiles')
    .select('gender, subscription_status')
    .eq('id', userId)
    .single()

  const isFemale = profile?.gender === 'female'
  const hasSubscription = profile?.subscription_status === 'active' || profile?.subscription_status === 'trial'
  const limit = isFemale || hasSubscription ? 3 : 0

  const { data: row } = await supabase
    .from('daily_limits')
    .select('super_likes_count')
    .eq('user_id', userId)
    .eq('date', dateStr)
    .maybeSingle()

  const used = row?.super_likes_count ?? 0
  const remaining = Math.max(0, limit - used)
  return { limit, used, remaining, canSuperLike: remaining > 0 }
}

export async function incrementDailyLikeCount(userId, timezone = 'UTC') {
  const dateStr = getTodayDateString(timezone)
  const { data: row } = await supabase
    .from('daily_limits')
    .select('likes_count')
    .eq('user_id', userId)
    .eq('date', dateStr)
    .maybeSingle()
  if (row) {
    await supabase
      .from('daily_limits')
      .update({ likes_count: (row.likes_count || 0) + 1 })
      .eq('user_id', userId)
      .eq('date', dateStr)
  } else {
    await supabase.from('daily_limits').insert({
      user_id: userId,
      date: dateStr,
      likes_count: 1,
      super_likes_count: 0
    })
  }
}

export async function incrementDailySuperLikeCount(userId, timezone = 'UTC') {
  const dateStr = getTodayDateString(timezone)
  const { data: row } = await supabase
    .from('daily_limits')
    .select('super_likes_count')
    .eq('user_id', userId)
    .eq('date', dateStr)
    .maybeSingle()
  if (row) {
    await supabase
      .from('daily_limits')
      .update({ super_likes_count: (row.super_likes_count || 0) + 1 })
      .eq('user_id', userId)
      .eq('date', dateStr)
  } else {
    await supabase.from('daily_limits').insert({
      user_id: userId,
      date: dateStr,
      likes_count: 0,
      super_likes_count: 1
    })
  }
}
